import { createContext, useContext } from "react";
import {
  calculatePercentage,
  IsMlsCompetition,
  calculateSubtractedValues,
  getCompetitionsToSkip,
  decimalRound,
  calculateSum,
} from "../../libraries/_helpers";
import { format } from "date-fns";

export const StatsContext = createContext();
StatsContext.displayName = "Stats";

export function useStatsData() {
  const context = useContext(StatsContext);
  if (context === undefined) {
    throw new Error(
      "useStatsData must be used within a <PlayerStats /> or <ClubStats/>"
    );
  }
  return context;
}

export function formatMatchStatsData(data, competitions) {
  if (!data) {
    return [];
  }
  const competitionsSkip = getCompetitionsToSkip();

  return data
    .filter((match) => !competitionsSkip.includes(match.competition_id))
    .map((el) => {
      const {
        game_title,
        kick_off,
        player_statistics,
        competition,
        competition_id,
      } = el;
      const {
        result,
        normalized_player_minutes,
        substitutions_in,
        passes_successful_sum,
        passes_sum,
        assists_shot_at_goal,
        assists,
        crosses_successful_sum,
        corner_kicks_sum,
        crosses_conversion_rate,
        passes_and_crosses_from_standard_sum,
        xG,
        shots_at_goal_left_leg,
        shots_at_goal_right_leg,
        shots_at_goal_head,
        penalties_successful,
        shots_at_goal_sum,
        shots_on_target,
        accurate_shot_rate,
        offsides,
        goals_conceded,
        cards_yellow,
        cards_red,
        cards_yellow_red,
        defensive_clearances,
        fouls_sum,
        ball_actions,
        goals,
        passes_conversion_rate,
        fouls_suffered,
        crosses_sum,
        penalties_caused,
        tackling_games_air_sum,
        tackling_games_air_won,
        first_and_second_assists,
      } = player_statistics;
      return {
        date: kick_off ? format(new Date(kick_off), "yyyy/LL/dd") : null,
        name: competition,
        match: game_title,
        results: result,
        mins_played: normalized_player_minutes,
        status:
          normalized_player_minutes > 0 && substitutions_in === 0
            ? "Starter"
            : "Substitute",
        goals: goals,
        expected_goals: decimalRound(xG, 2),
        goal_assist: IsMlsCompetition(competition_id)
          ? first_and_second_assists
          : assists,
        total_scoring_att: shots_at_goal_sum,
        ontarget_scoring_att: shots_on_target,
        accurate_pass_per: passes_conversion_rate * 100,
        fouls: calculateSubtractedValues(fouls_sum, fouls_suffered),
        was_fouled: fouls_suffered,
        total_offiside: offsides,
        yellow_card: cards_yellow ? cards_yellow : 0,
        total_red_card: calculateSum(cards_red, cards_yellow_red),
        //Passing
        accurate_pass: passes_successful_sum,
        total_pass: passes_sum,
        total_att_assist: assists_shot_at_goal,
        accurate_cross: crosses_successful_sum,
        corner_taken: corner_kicks_sum,
        accurate_cross_per: crosses_conversion_rate * 100,
        att_freekick_total: passes_and_crosses_from_standard_sum,
        total_cross: crosses_sum,
        //Attacking
        expected_goals_minus_goals: decimalRound(
          calculateSubtractedValues(xG, goals),
          2
        ),
        att_lf_total: shots_at_goal_left_leg,
        att_rf_total: shots_at_goal_right_leg,
        att_hd_total: shots_at_goal_head,
        att_pen_goal: penalties_successful,
        pka: penalties_caused,
        accurate_shooting_per: accurate_shot_rate * 100,
        total_offside: offsides,
        //Defending
        goals_conceded: goals_conceded,
        aerial_duel: tackling_games_air_sum,
        aerial_duel_per: calculatePercentage(
          tackling_games_air_won,
          tackling_games_air_sum
        ),
        total_clearance: defensive_clearances,
        fk_foul_lost: fouls_sum,
        touches: ball_actions,
      };
    });
}
